/*
 * How many points fill a class's reward jar. Younger grades get a shorter
 * jar. Rewards "grades" are free-text names the teacher types (e.g.
 * "Kindergarten", "1st", "2nd", "3rd"), so we parse a grade level from the
 * name and pick the band:
 *
 *   K, 1st, 2nd  →  24 points
 *   3rd and up   →  30 points
 *
 * Anything we can't parse falls back to the standard 30-point jar.
 */

export const JAR_TARGET_PRIMARY = 24; // Kindergarten–2nd grade
export const JAR_TARGET_DEFAULT = 30; // 3rd grade and up (and unrecognized names)

const WORD_LEVELS: Record<string, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
  sixth: 6,
  seventh: 7,
  eighth: 8,
};

/**
 * Best-effort grade level parsed from a free-text grade name. Kindergarten
 * (and pre-K / TK) is level 0. Returns null when nothing recognizable is found.
 */
export function gradeLevel(gradeName: string): number | null {
  const n = gradeName.trim().toLowerCase();
  if (!n) return null;

  // Kindergarten and below: "kindergarten", "pre-k", "tk", bare "k"/"kg".
  if (/\b(pre-?k|t-?k|kinder\w*|kg?)\b/.test(n)) return 0;

  // Ordinal digits: "1st", "2nd", "3rd", "4th".
  const ord = n.match(/\b(\d+)\s*(?:st|nd|rd|th)\b/);
  if (ord) return Number(ord[1]);

  // "grade 2", "gr 2", "grade-2", "g2".
  const g = n.match(/\bg(?:r|rade)?\s*-?\s*(\d+)\b/);
  if (g) return Number(g[1]);

  // Spelled-out ordinals: "second grade".
  for (const [word, lvl] of Object.entries(WORD_LEVELS)) {
    if (new RegExp(`\\b${word}\\b`).test(n)) return lvl;
  }

  // Bare leading number: "2", "2 - Smith".
  const bare = n.match(/^(\d+)\b/);
  if (bare) return Number(bare[1]);

  return null;
}

/** Default points to fill the jar for the given grade, from its name band. */
export function jarTargetForGrade(gradeName: string): number {
  const lvl = gradeLevel(gradeName);
  return lvl !== null && lvl <= 2 ? JAR_TARGET_PRIMARY : JAR_TARGET_DEFAULT;
}

/** Largest goal the teacher can set (matches the server/DB ceiling). */
export const MAX_GOAL = 60;

/**
 * The jar goal actually in effect for a grade: the teacher's manual override if
 * set, otherwise the grade-name default.
 */
export function effectiveJarTarget(gradeName: string, goalOverride: number | null): number {
  return goalOverride ?? jarTargetForGrade(gradeName);
}
